import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { firstValueFrom, of, catchError } from 'rxjs'
import { BITWISE_PARSER_VERSION, permissionEndpoints } from '@app/core/constants/valueConstants'
import { IndexDBHelper } from '../indexdb-service/indexdb-helper.service'
import {
    BitwiseParserCoordinator,
    type Permission,
    type PermissionFinal,
} from '@educational-initiatives/bitwise-engine'
import { UserStore } from '@core/services/user/user.store'
import { UserPermissions } from '@app/core/models/user.model'
import { AppConstants } from '@app/core/constants/app.constants'
import { environment } from 'src/environments/environment'

interface UserPreferences {
    version?: string
    permissions?: Permission[]
    permission_seq?: number[]
    [key: string]: unknown
}
/**
 * Service responsible for handling permission operations including fetching, caching, and bitwise parsing
 */
@Injectable({
    providedIn: 'root',
})
export class PermissionService {
    private http = inject(HttpClient)
    private indexDBHelper = inject(IndexDBHelper)
    private userStore = inject(UserStore)

    private permissionsCache: UserPermissions | null = null
    private bitwiseCache: PermissionFinal[] | null = null

    /**
     * Get permissions from server with proper 304 handling
     * @returns Promise resolving to permissions data or cached data if 304
     */
    async getPermissions(): Promise<UserPermissions | null> {
        try {
            const userPreferences = await this.indexDBHelper.getUserPreferences(
                AppConstants.SUB_APP_ID,
            )
            let url = `${environment.apiBaseUrl}${permissionEndpoints.GET_PERMISSIONS}/${AppConstants.SUB_APP_ID}`

            if (userPreferences?.['version']) {
                url += `?version=${userPreferences['version']}`
            }
            const response = await firstValueFrom(
                this.http.get<{ data?: unknown; fromCache?: boolean }>(url).pipe(
                    catchError((error: HttpErrorResponse) => {
                        if (error.status === 304) {
                            return of({ data: userPreferences, fromCache: true })
                        }
                        throw error
                    }),
                ),
            )

            if (response?.data && !response.fromCache) {
                await this.indexDBHelper.setPermissions(response.data, AppConstants.SUB_APP_ID)
                this.permissionsCache = response.data as UserPermissions
            } else if (response?.fromCache) {
                this.permissionsCache = userPreferences as UserPermissions
            }

            return this.permissionsCache
        } catch (error) {
            const cachedData = await this.indexDBHelper.getUserPreferences(AppConstants.SUB_APP_ID)
            if (cachedData) {
                this.permissionsCache = cachedData
                return cachedData
            }

            throw error
        }
    }

    /**
     * Get permissions and process them with bitwise parser
     * @returns Promise resolving to parsed bitwise permissions
     */
    async getPermissionsBitwise(): Promise<PermissionFinal[]> {
        if (!this.permissionsCache) {
            await this.getPermissions()
        }

        const userPreferences: UserPreferences =
            this.permissionsCache ||
            (await this.indexDBHelper.getUserPreferences(AppConstants.SUB_APP_ID))

        if (!userPreferences?.permissions || !userPreferences?.permission_seq) {
            throw new Error('Invalid permission data structure')
        }

        const coordinator = new BitwiseParserCoordinator(BITWISE_PARSER_VERSION)
        const parser = coordinator.getParser()
        parser.permissionList = userPreferences?.permissions
        parser.permissionSequence = userPreferences?.permission_seq
        const userPermissionCode = this.userStore.getUserData()?.permission ?? ''
        parser.permissionCode = userPermissionCode

        const result = parser.parsePermission()
        this.bitwiseCache = result

        return result
    }

    /**
     * Clear all caches
     */
    clearCache(): void {
        this.permissionsCache = null
        this.bitwiseCache = null
    }

    /**
     * Get cached permissions (if available)
     */
    getCachedPermissions(): UserPermissions | null {
        return this.permissionsCache
    }

    /**
     * Get cached bitwise permissions (if available)
     */
    getCachedBitwisePermissions(): PermissionFinal[] | null {
        return this.bitwiseCache
    }

    /**
     * Get permission by name
     */
    getPermissionByName(name: string): boolean | undefined {
        if (!this.bitwiseCache || !Array.isArray(this.bitwiseCache)) {
            return undefined
        }
        const permission = this.bitwiseCache.find(
            (perm: PermissionFinal) => perm.permissionName === name,
        )
        return permission ? (permission.isEnabled ?? false) : undefined
    }

    /**
     * Grants when IAM enables the permission, or when the permission is absent from the catalog
     * but every known catalog permission is enabled (typical super-admin / full-access users).
     */
    canAccessPermissionOrImplicitFullAccess(permissionName: string): boolean {
        const direct = this.getPermissionByName(permissionName)
        if (direct !== undefined) {
            return direct
        }

        const catalog = this.getCachedBitwisePermissions()
        if (!catalog?.length) {
            return false
        }

        return catalog.every((perm: PermissionFinal) => perm.isEnabled)
    }
}
